import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import gsap from 'gsap';
import ApiURL from '../../controllers/Api';
import { toast } from '../../utils/toast';
import CompanyLayout from '../../crm/CompanyLayout';
import EmployeeLayout from '../../crm/EmployeeLayout';

/* ------------------------------------------------------------------ */
/*  API + helpers (logic untouched)                                    */
/* ------------------------------------------------------------------ */

const getAuthHeaders = () => {
  const token = localStorage.getItem('token');
  return token ? { Authorization: `Bearer ${token}` } : {};
};

const getStoredUser = () => {
  try {
    return JSON.parse(localStorage.getItem('user') || '{}');
  } catch {
    return {};
  }
};

const JOB_TYPES = ['Full-time', 'Part-time', 'Contract', 'Internship', 'Temporary'];
const EXPERIENCE_LEVELS = ['Entry', 'Junior', 'Mid', 'Senior', 'Lead', 'Executive'];

const emptyForm = {
  title: '',
  location: '',
  job_type: 'Full-time',
  experience_level: 'Mid',
  salary_min: '',
  salary_max: '',
  company_logo: '',
  application_deadline: '',
  is_remote: false,
  description: '',
  requirements: '',
  skills: '',
};

const formatSalary = (min, max) => {
  const inr = (n) => `₹${Number(n).toLocaleString('en-IN')}`;
  if (!min && !max) return 'Not disclosed';
  if (!max) return `${inr(min)}+`;
  if (!min) return `Up to ${inr(max)}`;
  return `${inr(min)} – ${inr(max)}`;
};

const resumeUrlFor = (storedFile) =>
  storedFile ? `${ApiURL.replace(/backend\/?$/, 'uploads/resumes/')}${storedFile}` : null;

const formatDateTime = (value) => {
  if (!value) return '';
  const d = new Date(value);
  return d.toLocaleDateString('en-IN', { day: 'numeric', month: 'short', year: 'numeric' });
};

const formatDate = (dateString) => {
  if (!dateString) return 'Recently';
  const date = new Date(dateString);
  const diffDays = Math.ceil(Math.abs(new Date() - date) / (1000 * 60 * 60 * 24));
  if (diffDays <= 1) return 'Today';
  if (diffDays < 7) return `${diffDays} days ago`;
  if (diffDays < 30) return `${Math.floor(diffDays / 7)} weeks ago`;
  return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
};

/* ------------------------------------------------------------------ */
/*  Inline SVG icon set (no extra dependency)                          */
/* ------------------------------------------------------------------ */

const Icon = ({ path, className = 'h-4 w-4', stroke = 1.7 }) => (
  <svg
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth={stroke}
    strokeLinecap="round"
    strokeLinejoin="round"
    className={className}
    aria-hidden="true"
  >
    {path}
  </svg>
);

const Icons = {
  pin: <><path d="M20 10c0 6-8 12-8 12s-8-6-8-12a8 8 0 0 1 16 0Z" /><circle cx="12" cy="10" r="3" /></>,
  briefcase: <><rect x="3" y="7" width="18" height="13" rx="2" /><path d="M8 7V5a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2" /><path d="M3 12h18" /></>,
  level: <><path d="M4 20V10M10 20V4M16 20v-8M22 20H2" /></>,
  dollar: <><path d="M12 2v20M17 5H9.5a3.5 3.5 0 0 0 0 7h5a3.5 3.5 0 0 1 0 7H6" /></>,
  rupee: <><path d="M6 3h12M6 8h12M16 3c0 5-4 5-7 5 4 0 7 0 7 6l-4-3M9 13h2" /></>,
  edit: <><path d="M12 20h9" /><path d="M16.5 3.5a2.12 2.12 0 0 1 3 3L7 19l-4 1 1-4Z" /></>,
  close: <><path d="M18 6 6 18M6 6l12 12" /></>,
  mail: <><rect x="2" y="4" width="20" height="16" rx="2" /><path d="m22 7-10 6L2 7" /></>,
  phone: <><path d="M22 16.92v3a2 2 0 0 1-2.18 2 19.79 19.79 0 0 1-8.63-3.07 19.5 19.5 0 0 1-6-6 19.79 19.79 0 0 1-3.07-8.67A2 2 0 0 1 4.11 2h3a2 2 0 0 1 2 1.72c.13.96.36 1.9.7 2.81a2 2 0 0 1-.45 2.11L8.09 9.91a16 16 0 0 0 6 6l1.27-1.27a2 2 0 0 1 2.11-.45c.91.34 1.85.57 2.81.7A2 2 0 0 1 22 16.92Z" /></>,
  download: <><path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4M7 10l5 5 5-5M12 15V3" /></>,
  calendar: <><rect x="3" y="4" width="18" height="18" rx="2" /><path d="M16 2v4M8 2v4M3 10h18" /></>,
  image: <><rect x="3" y="3" width="18" height="18" rx="2" /><circle cx="9" cy="9" r="2" /><path d="m21 15-5-5L5 21" /></>,
  tag: <><path d="M12.59 2.59A2 2 0 0 0 11.17 2H4a2 2 0 0 0-2 2v7.17a2 2 0 0 0 .59 1.42l9 9a2 2 0 0 0 2.82 0l7.17-7.17a2 2 0 0 0 0-2.82l-9-9Z" /><circle cx="7.5" cy="7.5" r="1.5" /></>,
  doc: <><path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8Z" /><path d="M14 2v6h6M16 13H8M16 17H8M10 9H8" /></>,
  list: <><path d="M8 6h13M8 12h13M8 18h13M3 6h.01M3 12h.01M3 18h.01" /></>,
  users: <><path d="M16 21v-2a4 4 0 0 0-4-4H6a4 4 0 0 0-4 4v2" /><circle cx="9" cy="7" r="4" /><path d="M22 21v-2a4 4 0 0 0-3-3.87M16 3.13a4 4 0 0 1 0 7.75" /></>,
  layers: <><path d="m12 2 9 5-9 5-9-5 9-5Z" /><path d="m3 12 9 5 9-5M3 17l9 5 9-5" /></>,
  sparkle: <><path d="M12 3v4M12 17v4M5 12H1M23 12h-4M6.3 6.3 4.5 4.5M19.5 19.5l-1.8-1.8M17.7 6.3l1.8-1.8M4.5 19.5l1.8-1.8" /></>,
  arrow: <><path d="M5 12h14M13 6l6 6-6 6" /></>,
  globe: <><circle cx="12" cy="12" r="10" /><path d="M2 12h20M12 2a15 15 0 0 1 0 20 15 15 0 0 1 0-20Z" /></>,
};

/* ------------------------------------------------------------------ */
/*  Animated stat card (GSAP count-up)                                 */
/* ------------------------------------------------------------------ */

const StatCard = ({ value, label, icon, dark, accent }) => {
  const numRef = useRef(null);

  useEffect(() => {
    const node = numRef.current;
    if (!node) return;
    const obj = { v: 0 };
    const tween = gsap.to(obj, {
      v: value,
      duration: 0.9,
      ease: 'power2.out',
      onUpdate: () => {
        node.textContent = Math.round(obj.v).toLocaleString();
      },
    });
    return () => tween.kill();
  }, [value]);

  return (
    <motion.div
      initial={{ opacity: 0, y: 12 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.4, ease: 'easeOut' }}
      className={`relative overflow-hidden rounded-2xl p-4 ${
        dark
          ? 'bg-slate-900 text-white'
          : 'bg-white text-slate-900 ring-1 ring-slate-200 shadow-sm'
      }`}
    >
      <div
        className={`mb-3 flex h-8 w-8 items-center justify-center rounded-lg ${
          dark ? 'bg-white/10 text-white' : accent ? 'bg-emerald-50 text-emerald-600' : 'bg-slate-100 text-slate-600'
        }`}
      >
        <Icon path={icon} className="h-4 w-4" />
      </div>
      <div className={`text-xl font-bold tabular-nums ${accent && !dark ? 'text-emerald-600' : ''}`}>
        <span ref={numRef}>0</span>
      </div>
      <div className={`text-xs font-medium ${dark ? 'text-slate-400' : 'text-slate-500'}`}>{label}</div>
    </motion.div>
  );
};

/* ------------------------------------------------------------------ */
/*  Field wrapper with optional leading icon                           */
/* ------------------------------------------------------------------ */

const labelClass = 'mb-1.5 flex items-center gap-1.5 text-xs font-semibold text-slate-600';
const baseInput =
  'w-full rounded-xl border border-slate-200 bg-slate-50/60 px-3.5 py-2.5 text-sm text-slate-900 outline-none transition focus:border-slate-900 focus:bg-white focus:ring-2 focus:ring-slate-900/10 placeholder:text-slate-400';

const IconInput = ({ icon, ...props }) => (
  <div className="relative">
    <span className="pointer-events-none absolute left-3 top-1/2 -translate-y-1/2 text-slate-400">
      <Icon path={icon} className="h-4 w-4" />
    </span>
    <input {...props} className={`${baseInput} pl-9`} />
  </div>
);

/* ------------------------------------------------------------------ */
/*  Job card meta chip                                                 */
/* ------------------------------------------------------------------ */

const Chip = ({ icon, children, tone = 'slate' }) => {
  const tones = {
    slate: 'bg-slate-100 text-slate-600',
    emerald: 'bg-emerald-50 text-emerald-700',
  };
  return (
    <span className={`inline-flex items-center gap-1 rounded-lg px-2.5 py-1 text-xs font-medium ${tones[tone]}`}>
      {icon && <Icon path={icon} className="h-3.5 w-3.5" />}
      {children}
    </span>
  );
};

/* ------------------------------------------------------------------ */
/*  Main component                                                     */
/* ------------------------------------------------------------------ */

const CompanyJobs = ({ userData = null }) => {
  const navigate = useNavigate();
  const storedUser = getStoredUser();
  const Layout = storedUser?.user_type === 'employee' ? EmployeeLayout : CompanyLayout;
  const [form, setForm] = useState(emptyForm);
  const [submitting, setSubmitting] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [jobs, setJobs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [applicantsModal, setApplicantsModal] = useState(null); // { job, loading, list }

  const formRef = useRef(null);
  const companyName = userData?.company_name || storedUser?.company_name || 'Your company';

  useEffect(() => {
    fetchMyJobs();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // GSAP reveal for the form panel on mount
  useEffect(() => {
    if (!formRef.current) return;
    const ctx = gsap.context(() => {
      gsap.from(formRef.current, {
        opacity: 0,
        y: 24,
        duration: 0.6,
        ease: 'power3.out',
      });
    }, formRef);
    return () => ctx.revert();
  }, []);

  const stats = useMemo(() => {
    const active = jobs.filter((job) => job.status === 'active').length;
    const applicants = jobs.reduce((sum, job) => sum + Number(job.applicant_count || 0), 0);
    return { total: jobs.length, active, applicants };
  }, [jobs]);

  const fetchMyJobs = async () => {
    setLoading(true);
    try {
      const response = await fetch(`${ApiURL}my_jobs.php`, { headers: getAuthHeaders() });
      const result = await response.json().catch(() => ({}));
      if (!response.ok || result.success === false) {
        throw new Error(result.message || 'Failed to load your jobs');
      }
      setJobs(result.data || []);
    } catch (error) {
      toast.error(error.message || 'Failed to load your jobs');
    } finally {
      setLoading(false);
    }
  };

  const updateField = (field, value) => setForm((prev) => ({ ...prev, [field]: value }));

  const startEdit = (job) => {
    setEditingId(job.id);
    setForm({
      title: job.title || '',
      location: job.location || '',
      job_type: job.job_type || 'Full-time',
      experience_level: job.experience_level || 'Mid',
      salary_min: job.salary_min ?? '',
      salary_max: job.salary_max ?? '',
      company_logo: job.company_logo || '',
      application_deadline: job.deadline ? String(job.deadline).slice(0, 10) : '',
      is_remote: Boolean(job.is_remote),
      description: job.description || '',
      requirements: job.requirements || '',
      skills: Array.isArray(job.tags) ? job.tags.join(', ') : '',
    });
    if (formRef.current) {
      formRef.current.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }
  };

  const cancelEdit = () => {
    setEditingId(null);
    setForm(emptyForm);
  };

  const handleSubmit = async (event) => {
    event.preventDefault();

    if (!form.title.trim() || !form.location.trim() || !form.description.trim()) {
      toast.warning('Please fill in the title, location, and description');
      return;
    }

    setSubmitting(true);
    try {
      const payload = {
        ...form,
        salary_min: form.salary_min ? Number(form.salary_min) : '',
        salary_max: form.salary_max ? Number(form.salary_max) : '',
        skills: form.skills
          ? form.skills.split(',').map((skill) => skill.trim()).filter(Boolean)
          : [],
      };

      const isEditing = Boolean(editingId);
      const endpoint = isEditing ? 'update_job.php' : 'post_job.php';
      if (isEditing) {
        payload.job_id = editingId;
      }

      const response = await fetch(`${ApiURL}${endpoint}`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', ...getAuthHeaders() },
        body: JSON.stringify(payload),
      });
      const result = await response.json().catch(() => ({}));
      if (!response.ok || result.success === false) {
        throw new Error(result.message || 'Failed to save job');
      }

      toast.success(isEditing ? 'Job updated successfully' : 'Job posted and live on the feed');
      setForm(emptyForm);
      setEditingId(null);
      fetchMyJobs();
    } catch (error) {
      toast.error(error.message || 'Failed to save job');
    } finally {
      setSubmitting(false);
    }
  };

  const openApplicants = async (job) => {
    setApplicantsModal({ job, loading: true, list: [] });
    try {
      const response = await fetch(`${ApiURL}job_applicants.php?job_id=${job.id}`, {
        headers: getAuthHeaders(),
      });
      const result = await response.json().catch(() => ({}));
      if (!response.ok || result.success === false) {
        throw new Error(result.message || 'Failed to load applicants');
      }
      setApplicantsModal({ job, loading: false, list: result.data?.applicants || [] });
    } catch (error) {
      toast.error(error.message || 'Failed to load applicants');
      setApplicantsModal(null);
    }
  };

  const handleAction = async (jobId, action) => {
    if (action === 'delete' && !window.confirm('Delete this job posting permanently?')) {
      return;
    }
    try {
      const response = await fetch(`${ApiURL}my_jobs.php`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', ...getAuthHeaders() },
        body: JSON.stringify({ job_id: jobId, action }),
      });
      const result = await response.json().catch(() => ({}));
      if (!response.ok || result.success === false) {
        throw new Error(result.message || 'Action failed');
      }
      toast.success(result.message || 'Updated');
      fetchMyJobs();
    } catch (error) {
      toast.error(error.message || 'Action failed');
    }
  };

  return (
    <Layout
      title="Post a Job"
      description={`Publish roles for ${companyName}. Everything you post appears live on the public feed candidates browse.`}
      headerAction={
        <motion.button
          whileHover={{ y: -1 }}
          whileTap={{ scale: 0.97 }}
          onClick={() => navigate('/jobs')}
          className="inline-flex items-center gap-1.5 rounded-lg bg-emerald-50 px-3 py-1.5 text-xs font-semibold text-emerald-700 transition-colors hover:bg-emerald-100"
        >
          <Icon path={Icons.globe} className="h-3.5 w-3.5" />
          View public feed
        </motion.button>
      }
    >
      {/* Stats */}
      <div className="mb-6 grid max-w-md grid-cols-3 gap-3">
        <StatCard value={stats.total} label="Total posts" icon={Icons.layers} dark />
        <StatCard value={stats.active} label="Active" icon={Icons.sparkle} accent />
        <StatCard value={stats.applicants} label="Applicants" icon={Icons.users} />
      </div>

      <section className="grid gap-6 lg:grid-cols-[0.92fr_1.08fr]">
        {/* ---------------- Post a job form ---------------- */}
        <form
          ref={formRef}
          onSubmit={handleSubmit}
          className="h-fit rounded-2xl bg-white p-5 shadow-sm ring-1 ring-slate-200 sm:p-6 lg:sticky lg:top-24"
        >
          <div className="flex items-center gap-3 border-b border-slate-100 pb-4">
            <div className={`flex h-10 w-10 items-center justify-center rounded-xl text-white ${editingId ? 'bg-emerald-600' : 'bg-slate-900'}`}>
              <Icon path={editingId ? Icons.edit : Icons.briefcase} className="h-5 w-5" />
            </div>
            <div className="flex-1">
              <h2 className="text-base font-bold text-slate-900">{editingId ? 'Edit job' : 'Post a new job'}</h2>
              <p className="text-xs text-slate-500">
                {editingId ? 'Update the details and save your changes' : 'Fields marked * are required'}
              </p>
            </div>
            {editingId && (
              <button
                type="button"
                onClick={cancelEdit}
                className="rounded-lg border border-slate-200 px-2.5 py-1 text-xs font-semibold text-slate-600 transition hover:bg-slate-50"
              >
                Cancel
              </button>
            )}
          </div>

          <div className="mt-5 space-y-4">
            <div>
              <label className={labelClass}>
                <Icon path={Icons.briefcase} className="h-3.5 w-3.5" /> Job title *
              </label>
              <input
                className={baseInput}
                placeholder="e.g. Senior Frontend Engineer"
                value={form.title}
                onChange={(e) => updateField('title', e.target.value)}
              />
            </div>

            <div className="grid gap-4 sm:grid-cols-2">
              <div>
                <label className={labelClass}>
                  <Icon path={Icons.pin} className="h-3.5 w-3.5" /> Location *
                </label>
                <IconInput
                  icon={Icons.pin}
                  placeholder="Bengaluru / Remote"
                  value={form.location}
                  onChange={(e) => updateField('location', e.target.value)}
                />
              </div>
              <div className="flex items-end">
                <label className="flex w-full cursor-pointer items-center gap-2.5 rounded-xl border border-slate-200 bg-slate-50/60 px-3.5 py-2.5 transition hover:border-emerald-300 hover:bg-emerald-50/40">
                  <input
                    type="checkbox"
                    className="h-4.5 w-4.5 rounded accent-emerald-600"
                    checked={form.is_remote}
                    onChange={(e) => updateField('is_remote', e.target.checked)}
                  />
                  <span className="flex items-center gap-1.5 text-sm font-semibold text-slate-700">
                    <Icon path={Icons.globe} className="h-4 w-4 text-slate-400" /> Remote friendly
                  </span>
                </label>
              </div>
            </div>

            <div className="grid gap-4 sm:grid-cols-2">
              <div>
                <label className={labelClass}>
                  <Icon path={Icons.briefcase} className="h-3.5 w-3.5" /> Job type *
                </label>
                <select
                  className={baseInput}
                  value={form.job_type}
                  onChange={(e) => updateField('job_type', e.target.value)}
                >
                  {JOB_TYPES.map((type) => (
                    <option key={type} value={type}>{type}</option>
                  ))}
                </select>
              </div>
              <div>
                <label className={labelClass}>
                  <Icon path={Icons.level} className="h-3.5 w-3.5" /> Experience *
                </label>
                <select
                  className={baseInput}
                  value={form.experience_level}
                  onChange={(e) => updateField('experience_level', e.target.value)}
                >
                  {EXPERIENCE_LEVELS.map((level) => (
                    <option key={level} value={level}>{level}</option>
                  ))}
                </select>
              </div>
            </div>

            <div className="grid gap-4 sm:grid-cols-2">
              <div>
                <label className={labelClass}>
                  <Icon path={Icons.rupee} className="h-3.5 w-3.5" /> Min salary (₹/yr)
                </label>
                <IconInput
                  icon={Icons.rupee}
                  type="number"
                  min="0"
                  placeholder="500000"
                  value={form.salary_min}
                  onChange={(e) => updateField('salary_min', e.target.value)}
                />
              </div>
              <div>
                <label className={labelClass}>
                  <Icon path={Icons.rupee} className="h-3.5 w-3.5" /> Max salary (₹/yr)
                </label>
                <IconInput
                  icon={Icons.rupee}
                  type="number"
                  min="0"
                  placeholder="900000"
                  value={form.salary_max}
                  onChange={(e) => updateField('salary_max', e.target.value)}
                />
              </div>
            </div>

            <div className="grid gap-4 sm:grid-cols-2">
              <div>
                <label className={labelClass}>
                  <Icon path={Icons.calendar} className="h-3.5 w-3.5" /> Deadline
                </label>
                <input
                  type="date"
                  className={baseInput}
                  value={form.application_deadline}
                  onChange={(e) => updateField('application_deadline', e.target.value)}
                />
              </div>
              <div>
                <label className={labelClass}>
                  <Icon path={Icons.image} className="h-3.5 w-3.5" /> Logo URL
                </label>
                <IconInput
                  icon={Icons.image}
                  placeholder="https://..."
                  value={form.company_logo}
                  onChange={(e) => updateField('company_logo', e.target.value)}
                />
              </div>
            </div>

            <div>
              <label className={labelClass}>
                <Icon path={Icons.tag} className="h-3.5 w-3.5" /> Skills (comma separated)
              </label>
              <IconInput
                icon={Icons.tag}
                placeholder="React, TypeScript, Node.js"
                value={form.skills}
                onChange={(e) => updateField('skills', e.target.value)}
              />
            </div>

            <div>
              <label className={labelClass}>
                <Icon path={Icons.doc} className="h-3.5 w-3.5" /> Description *
              </label>
              <textarea
                rows={4}
                className={`${baseInput} resize-y`}
                placeholder="Describe the role, responsibilities, and what makes it exciting."
                value={form.description}
                onChange={(e) => updateField('description', e.target.value)}
              />
            </div>

            <div>
              <label className={labelClass}>
                <Icon path={Icons.list} className="h-3.5 w-3.5" /> Requirements
              </label>
              <textarea
                rows={3}
                className={`${baseInput} resize-y`}
                placeholder="Must-have qualifications and nice-to-haves."
                value={form.requirements}
                onChange={(e) => updateField('requirements', e.target.value)}
              />
            </div>
          </div>

          <motion.button
            type="submit"
            disabled={submitting}
            whileHover={{ y: submitting ? 0 : -1 }}
            whileTap={{ scale: submitting ? 1 : 0.98 }}
            className={`mt-5 flex w-full items-center justify-center gap-2 rounded-xl px-5 py-3 text-sm font-semibold text-white transition disabled:cursor-not-allowed disabled:opacity-60 ${
              editingId ? 'bg-emerald-600 hover:bg-emerald-700' : 'bg-slate-900 hover:bg-slate-800'
            }`}
          >
            {submitting ? (
              <>
                <span className="h-4 w-4 animate-spin rounded-full border-2 border-white/40 border-t-white" />
                {editingId ? 'Saving…' : 'Publishing…'}
              </>
            ) : (
              <>
                {editingId ? 'Save changes' : 'Publish job'}
                <Icon path={editingId ? Icons.edit : Icons.arrow} className="h-4 w-4" />
              </>
            )}
          </motion.button>
        </form>

        {/* ---------------- Posted jobs list ---------------- */}
        <div>
          <div className="mb-4 flex items-center justify-between">
            <h2 className="flex items-center gap-2 text-base font-bold text-slate-900">
              <Icon path={Icons.layers} className="h-4 w-4 text-slate-400" />
              Your job posts
              {!loading && (
                <span className="rounded-full bg-slate-100 px-2 py-0.5 text-xs font-semibold text-slate-500">
                  {jobs.length}
                </span>
              )}
            </h2>
            <motion.button
              whileHover={{ y: -1 }}
              whileTap={{ scale: 0.97 }}
              onClick={() => navigate('/jobs')}
              className="inline-flex items-center gap-1.5 rounded-full bg-white px-3.5 py-2 text-xs font-semibold text-slate-700 shadow-sm ring-1 ring-slate-200 hover:bg-slate-50"
            >
              <Icon path={Icons.globe} className="h-3.5 w-3.5" />
              Public feed
            </motion.button>
          </div>

          {loading ? (
            <div className="rounded-2xl bg-white p-10 text-center shadow-sm ring-1 ring-slate-200">
              <div className="mx-auto h-10 w-10 animate-spin rounded-full border-2 border-slate-200 border-t-slate-900" />
              <p className="mt-3 text-sm text-slate-500">Loading your jobs…</p>
            </div>
          ) : jobs.length === 0 ? (
            <motion.div
              initial={{ opacity: 0, y: 12 }}
              animate={{ opacity: 1, y: 0 }}
              className="rounded-2xl bg-white p-10 text-center shadow-sm ring-1 ring-slate-200"
            >
              <div className="mx-auto flex h-12 w-12 items-center justify-center rounded-2xl bg-slate-100 text-slate-400">
                <Icon path={Icons.briefcase} className="h-6 w-6" />
              </div>
              <h3 className="mt-4 text-base font-bold text-slate-900">No jobs posted yet</h3>
              <p className="mt-1 text-sm text-slate-500">Use the form to publish your first role. It goes live instantly.</p>
            </motion.div>
          ) : (
            <motion.div layout className="space-y-3">
              <AnimatePresence mode="popLayout">
                {jobs.map((job, i) => (
                  <motion.article
                    key={job.id}
                    layout
                    initial={{ opacity: 0, y: 16 }}
                    animate={{ opacity: 1, y: 0 }}
                    exit={{ opacity: 0, scale: 0.96, transition: { duration: 0.2 } }}
                    transition={{ duration: 0.35, delay: i * 0.05, ease: 'easeOut' }}
                    whileHover={{ y: -2 }}
                    className="rounded-2xl bg-white p-4 shadow-sm ring-1 ring-slate-200 transition-shadow hover:shadow-md sm:p-5"
                  >
                    <div className="flex items-start justify-between gap-3">
                      <div className="flex min-w-0 gap-3">
                        <img
                          src={
                            job.company_logo ||
                            `https://ui-avatars.com/api/?name=${encodeURIComponent(job.company_name)}&background=0f172a&color=fff&size=128`
                          }
                          alt={job.company_name}
                          className="h-12 w-12 shrink-0 rounded-xl border border-slate-200 object-cover"
                        />
                        <div className="min-w-0">
                          <div className="flex flex-wrap items-center gap-2">
                            <h3 className="truncate text-sm font-bold text-slate-900">{job.title}</h3>
                            <span
                              className={`inline-flex items-center gap-1 rounded-full px-2 py-0.5 text-[11px] font-semibold ${
                                job.status === 'active'
                                  ? 'bg-emerald-100 text-emerald-700'
                                  : 'bg-slate-200 text-slate-600'
                              }`}
                            >
                              <span className={`h-1.5 w-1.5 rounded-full ${job.status === 'active' ? 'bg-emerald-500' : 'bg-slate-400'}`} />
                              {job.status === 'active' ? 'Active' : 'Closed'}
                            </span>
                            {job.is_featured && (
                              <span className="rounded-full bg-amber-100 px-2 py-0.5 text-[11px] font-semibold text-amber-700">
                                Featured
                              </span>
                            )}
                          </div>
                          <div className="mt-2 flex flex-wrap gap-1.5">
                            <Chip icon={Icons.pin}>{job.location}</Chip>
                            <Chip icon={Icons.briefcase}>{job.job_type}</Chip>
                            <Chip icon={Icons.level}>{job.experience_level}</Chip>
                            <Chip icon={Icons.rupee} tone="emerald">{formatSalary(job.salary_min, job.salary_max)}</Chip>
                          </div>
                        </div>
                      </div>
                      <button
                        type="button"
                        onClick={() => openApplicants(job)}
                        className="group shrink-0 rounded-xl px-2 py-1 text-right transition hover:bg-emerald-50"
                        title="View applicants"
                      >
                        <div className="text-lg font-bold tabular-nums text-slate-900 group-hover:text-emerald-700">{job.applicant_count}</div>
                        <div className="flex items-center gap-1 text-[11px] text-slate-500 group-hover:text-emerald-700">
                          <Icon path={Icons.users} className="h-3 w-3" />
                          applicants
                        </div>
                      </button>
                    </div>

                    {!!job.tags?.length && (
                      <div className="mt-3 flex flex-wrap gap-1.5">
                        {job.tags.slice(0, 8).map((tag) => (
                          <span key={tag} className="rounded-lg bg-slate-50 px-2.5 py-1 text-[11px] font-medium text-slate-600 ring-1 ring-slate-100">
                            {tag}
                          </span>
                        ))}
                      </div>
                    )}

                    <div className="mt-3 flex flex-col gap-2.5 border-t border-slate-100 pt-3 sm:flex-row sm:items-center sm:justify-between">
                      <span className="flex items-center gap-1.5 text-xs text-slate-400">
                        <Icon path={Icons.calendar} className="h-3.5 w-3.5" />
                        Posted {formatDate(job.posted_at)}
                      </span>
                      <div className="flex flex-wrap gap-2">
                        <motion.button
                          whileTap={{ scale: 0.96 }}
                          onClick={() => openApplicants(job)}
                          className="inline-flex items-center gap-1 rounded-lg border border-slate-200 px-3 py-1.5 text-xs font-semibold text-slate-700 transition hover:bg-slate-50"
                        >
                          <Icon path={Icons.users} className="h-3.5 w-3.5" />
                          Applicants
                        </motion.button>
                        <motion.button
                          whileTap={{ scale: 0.96 }}
                          onClick={() => startEdit(job)}
                          className="inline-flex items-center gap-1 rounded-lg border border-emerald-200 px-3 py-1.5 text-xs font-semibold text-emerald-700 transition hover:bg-emerald-50"
                        >
                          <Icon path={Icons.edit} className="h-3.5 w-3.5" />
                          Edit
                        </motion.button>
                        {job.status === 'active' ? (
                          <motion.button
                            whileTap={{ scale: 0.96 }}
                            onClick={() => handleAction(job.id, 'close')}
                            className="rounded-lg border border-slate-200 px-3 py-1.5 text-xs font-semibold text-slate-700 transition hover:bg-slate-50"
                          >
                            Close
                          </motion.button>
                        ) : (
                          <motion.button
                            whileTap={{ scale: 0.96 }}
                            onClick={() => handleAction(job.id, 'activate')}
                            className="rounded-lg border border-emerald-200 px-3 py-1.5 text-xs font-semibold text-emerald-700 transition hover:bg-emerald-50"
                          >
                            Reopen
                          </motion.button>
                        )}
                        <motion.button
                          whileTap={{ scale: 0.96 }}
                          onClick={() => handleAction(job.id, 'delete')}
                          className="rounded-lg border border-red-200 px-3 py-1.5 text-xs font-semibold text-red-600 transition hover:bg-red-50"
                        >
                          Delete
                        </motion.button>
                      </div>
                    </div>
                  </motion.article>
                ))}
              </AnimatePresence>
            </motion.div>
          )}
        </div>
      </section>

      {/* ---------------- Applicants modal ---------------- */}
      <AnimatePresence>
        {applicantsModal && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 z-50 flex items-end justify-center bg-slate-900/50 p-0 backdrop-blur-sm sm:items-center sm:p-4"
            onClick={() => setApplicantsModal(null)}
          >
            <motion.div
              initial={{ opacity: 0, y: 30, scale: 0.98 }}
              animate={{ opacity: 1, y: 0, scale: 1 }}
              exit={{ opacity: 0, y: 30, scale: 0.98 }}
              transition={{ duration: 0.25, ease: 'easeOut' }}
              onClick={(e) => e.stopPropagation()}
              className="flex max-h-[88vh] w-full max-w-2xl flex-col overflow-hidden rounded-t-3xl bg-white shadow-2xl sm:rounded-3xl"
            >
              <div className="flex items-start justify-between gap-3 border-b border-slate-100 p-5">
                <div className="min-w-0">
                  <h3 className="flex items-center gap-2 text-base font-bold text-slate-900">
                    <Icon path={Icons.users} className="h-4 w-4 text-emerald-600" />
                    Applicants
                  </h3>
                  <p className="mt-0.5 truncate text-sm text-slate-500">{applicantsModal.job.title}</p>
                </div>
                <button
                  onClick={() => setApplicantsModal(null)}
                  className="rounded-lg p-1.5 text-slate-400 transition hover:bg-slate-100 hover:text-slate-700"
                >
                  <Icon path={Icons.close} className="h-5 w-5" />
                </button>
              </div>

              <div className="flex-1 overflow-y-auto p-5">
                {applicantsModal.loading ? (
                  <div className="py-12 text-center">
                    <div className="mx-auto h-10 w-10 animate-spin rounded-full border-2 border-slate-200 border-t-slate-900" />
                    <p className="mt-3 text-sm text-slate-500">Loading applicants…</p>
                  </div>
                ) : applicantsModal.list.length === 0 ? (
                  <div className="py-12 text-center">
                    <div className="mx-auto flex h-12 w-12 items-center justify-center rounded-2xl bg-slate-100 text-slate-400">
                      <Icon path={Icons.users} className="h-6 w-6" />
                    </div>
                    <h4 className="mt-4 text-base font-bold text-slate-900">No applicants yet</h4>
                    <p className="mt-1 text-sm text-slate-500">Candidates who apply will show up here.</p>
                  </div>
                ) : (
                  <div className="space-y-3">
                    {applicantsModal.list.map((a) => {
                      const resumeUrl = resumeUrlFor(a.resume_file);
                      return (
                        <div
                          key={a.application_id}
                          className="rounded-2xl border border-slate-200 p-4 transition hover:border-emerald-200 hover:bg-emerald-50/30"
                        >
                          <div className="flex items-start justify-between gap-3">
                            <div className="flex min-w-0 gap-3">
                              <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-full bg-slate-900 text-sm font-bold text-white">
                                {(a.name || 'C').trim().charAt(0).toUpperCase()}
                              </div>
                              <div className="min-w-0">
                                <p className="truncate text-sm font-bold text-slate-900">{a.name}</p>
                                <div className="mt-1 flex flex-col gap-1 text-xs text-slate-500">
                                  {a.email && (
                                    <a href={`mailto:${a.email}`} className="inline-flex items-center gap-1.5 hover:text-emerald-700">
                                      <Icon path={Icons.mail} className="h-3.5 w-3.5" /> {a.email}
                                    </a>
                                  )}
                                  {a.phone && (
                                    <a href={`tel:${a.phone}`} className="inline-flex items-center gap-1.5 hover:text-emerald-700">
                                      <Icon path={Icons.phone} className="h-3.5 w-3.5" /> {a.phone}
                                    </a>
                                  )}
                                  {a.location && (
                                    <span className="inline-flex items-center gap-1.5">
                                      <Icon path={Icons.pin} className="h-3.5 w-3.5" /> {a.location}
                                    </span>
                                  )}
                                </div>
                              </div>
                            </div>
                            <div className="shrink-0 text-right">
                              <div className="inline-flex items-center rounded-full bg-emerald-100 px-2.5 py-1 text-xs font-bold text-emerald-700">
                                {a.match_score}% match
                              </div>
                              <div className="mt-1 text-[11px] text-slate-400">Applied {formatDateTime(a.applied_at)}</div>
                            </div>
                          </div>
                          <div className="mt-3 flex items-center justify-between border-t border-slate-100 pt-3">
                            <span className="text-xs text-slate-500">
                              {a.resume_filename || 'No resume on file'}
                            </span>
                            {resumeUrl ? (
                              <a
                                href={resumeUrl}
                                target="_blank"
                                rel="noopener noreferrer"
                                className="inline-flex items-center gap-1.5 rounded-lg bg-slate-900 px-3 py-1.5 text-xs font-semibold text-white transition hover:bg-slate-800"
                              >
                                <Icon path={Icons.download} className="h-3.5 w-3.5" />
                                Resume
                              </a>
                            ) : null}
                          </div>
                        </div>
                      );
                    })}
                  </div>
                )}
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </Layout>
  );
};

export default CompanyJobs;
